import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { AlertContent, Button, CentBasedInputCurrency, H2, Label, ModalContent, ModalFactory, ModalFooter, ModalHeader, ModalLayout, ValidationMessage } from 'components';
import { lineHeight, theme, toRem } from 'design';
import { IPayee, ITipCalculatorPayee } from 'types';
import { calculateInstant, centsToDollarsDisplay } from 'utils';

export interface IHoldbackModalProps {
  defaultValue?: number;
  payee: IPayee | ITipCalculatorPayee;
  disabled?: boolean;
  hideModal: () => void;
  onChange: (holdback: number) => void;
}

const HoldbackFieldContainer = styled.div`
  display: flex;
  flex-flow: column;
  align-items: flex-start;
  width: 100%;
  padding: ${toRem(theme.space.lg)};
  background: ${theme.color.neutral[50]};
  border: 1px solid ${theme.color.neutral[200]};
  border-radius: ${theme.borderRadius.small}px;

  label {
    color: ${theme.color.neutral[500]};
    font-size: ${toRem(theme.font.size.xs)};
    line-height: ${lineHeight(theme.font.size.xs)};
    margin-bottom: ${toRem(theme.space.sm)};
  }

  .error-message {
    margin-top: ${toRem(theme.space.xs)};
  }
`;

const HoldbackModalFooter = styled(ModalFooter)`
  padding-top: ${toRem(theme.space.xl)};

  button:first-child,
  button:last-child {
    justify-self: stretch;
  }
`;

const HoldbackModalHeader = styled(ModalHeader)`
  padding-bottom: ${toRem(theme.space.xl)};
`;

const HoldbackModalLayout = styled(ModalLayout)`
  width: 100%;
  max-width: ${toRem(512)};
`;

const PayeeInfo = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: ${toRem(theme.space.lg)};
  text-align: center;

  @media (min-width: ${theme.breakpoint.small}px) {
    flex-flow: row nowrap;
  }
`;

const PayeeName = styled.span`
  font-size: ${toRem(theme.font.size.lg)};
  font-weight: ${theme.font.weight.bold};
  color: ${theme.color.neutral[900]};
  text-align: left;
`;

const PayeeId = styled.span`
  font-size: ${toRem(theme.font.size.xxs)};
  font-weight: ${theme.font.weight.regular};
  color: ${theme.color.neutral[500]};
  text-transform: uppercase;
  word-break: break-all;
  text-align: right;
`;

const HoldbackModal: React.FC<IHoldbackModalProps> = ({ defaultValue, payee, disabled, hideModal, onChange }) => {
  const [value, setValue] = useState(defaultValue || 0);
  const dataCy = useRef('holdback-modal').current;
  const employeeId = 'provider' in payee ? payee.provider.customerEmployeeId : payee.employee?.employeeId;
  const instant = calculateInstant({ ...payee, holdbackAmount: 0 });
  const holdbackError = value >= instant;

  const onHoldbackChange = (value: string) => {
    const parsedValue = parseInt(value, 10);
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(parsedValue)) return;

    setValue(parsedValue);
  };

  const onSave = () => {
    onChange(value);
    hideModal();
  };

  return (
    <ModalFactory customModal onClose={hideModal} isActive>
      {disabled ? (
        <AlertContent
          title="You are not authorized to edit holdback amounts."
          message="Contact your manager or Kickfin support for assistance."
          rightActionLabel="Go Back"
          onClose={hideModal}
          onRightAction={hideModal}
          hideLeftAction
          maxWidth={440}
          dataCy={dataCy}
        />
      ) : (
        <HoldbackModalLayout isActive hideCloseButton onClose={hideModal} data-cy={dataCy}>
          <HoldbackModalHeader>
            <H2 data-cy={`${dataCy}-header`} fontWeight="bold">
              Edit Holdback
            </H2>
          </HoldbackModalHeader>
          <ModalContent>
            <HoldbackFieldContainer>
              <PayeeInfo>
                <PayeeName data-cy={`${dataCy}-payee-name`}>{payee.name}</PayeeName>
                {employeeId && <PayeeId data-cy={`${dataCy}-payee-id`}>ID: {employeeId}</PayeeId>}
              </PayeeInfo>
              <Label>Holdback</Label>
              <CentBasedInputCurrency data-cy={`${dataCy}-currency-input`} value={value.toString()} onChange={onHoldbackChange} allowZero hasError={holdbackError} />
              {holdbackError && (
                <ValidationMessage hasError className="error-message" data-cy={`${dataCy}-error-msg`}>
                  Holdback must be less than instant payment ({centsToDollarsDisplay(instant)})
                </ValidationMessage>
              )}
            </HoldbackFieldContainer>
          </ModalContent>
          <HoldbackModalFooter columns={2}>
            <Button data-cy="cancel-holdback-btn" appearance="white" label="Cancel" onClick={hideModal} />
            <Button data-cy="save-holdback-btn" label="Save" onClick={onSave} disabled={holdbackError} />
          </HoldbackModalFooter>
        </HoldbackModalLayout>
      )}
    </ModalFactory>
  );
};

export default HoldbackModal;
